import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

// Types
interface ImportFile {
  id: number;
  originalFilename: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  importedCount: number | null;
  createdAt: string;
}

interface FilePreview {
  headers: string[];
  sampleData: any[][];
  totalRows: number;
  fileType: 'excel' | 'csv';
}

interface ColumnMapping {
  siretColumn: string | null;
  contactNameColumn: string | null;
  contactPhoneColumn: string | null;
  contactEmailColumn: string | null;
  descriptionColumns: string[];
}

const ImportAdherents: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [file, setFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [importedFile, setImportedFile] = useState<ImportFile | null>(null);
  const [filePreview, setFilePreview] = useState<FilePreview | null>(null);
  const [columnMapping, setColumnMapping] = useState<ColumnMapping>({
    siretColumn: null,
    contactNameColumn: null,
    contactPhoneColumn: null,
    contactEmailColumn: null,
    descriptionColumns: [],
  });
  const [currentMappingField, setCurrentMappingField] = useState<keyof ColumnMapping | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [processingResult, setProcessingResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset error when changing steps
  useEffect(() => {
    setError(null);
  }, [currentStep]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleFileUpload = async () => {
    if (!file) {
      setError('Veuillez sélectionner un fichier');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setError(null);
    setParseError(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/adherents/import/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / (progressEvent.total || 1)
            );
            setUploadProgress(percentCompleted);
          },
        }
      );

      setImportedFile(response.data);
      // Move to the next step
      setCurrentStep(2);
      // Get file preview
      await getFilePreview(response.data.id);
    } catch (error: any) {
      console.error('Error uploading file:', error);
      setError(
        error.response?.data?.message ||
          'Une erreur est survenue lors du téléchargement du fichier'
      );
    } finally {
      setIsUploading(false);
    }
  };

  const getFilePreview = async (fileId: number) => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_BASE_URL}/adherents/import/preview/${fileId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      setFilePreview(response.data);
      
      // Initialize the mapping process with the first field (SIRET)
      setCurrentMappingField('siretColumn');
    } catch (error: any) {
      console.error('Error getting file preview:', error);
      
      // Check if this is a parsing error
      if (error.response?.data?.parseError) {
        setParseError(error.response.data.parseError);
      } else {
        setError(
          error.response?.data?.message ||
            'Une erreur est survenue lors de la prévisualisation du fichier'
        );
      }
      
      // Go back to step 1 if preview fails
      setCurrentStep(1);
    }
  };

  const handleColumnSelect = (columnName: string) => {
    if (!currentMappingField) return;
    
    if (currentMappingField === 'descriptionColumns') {
      // Handle multi-select for description columns
      setColumnMapping((prev) => {
        const updatedDescriptionColumns = prev.descriptionColumns.includes(columnName)
          ? prev.descriptionColumns.filter((col) => col !== columnName)
          : [...prev.descriptionColumns, columnName];
        
        return {
          ...prev,
          descriptionColumns: updatedDescriptionColumns,
        };
      });
    } else {
      // Handle single-select for other columns
      setColumnMapping((prev) => ({
        ...prev,
        [currentMappingField]: columnName,
      }));
      
      // Move to the next field
      moveToNextField();
    }
  };
  
  const moveToNextField = () => {
    const fields: (keyof ColumnMapping)[] = [
      'siretColumn',
      'contactNameColumn',
      'contactPhoneColumn',
      'contactEmailColumn',
      'descriptionColumns'
    ];
    
    const currentIndex = currentMappingField ? fields.indexOf(currentMappingField) : -1;
    
    if (currentIndex >= 0 && currentIndex < fields.length - 1) {
      setCurrentMappingField(fields[currentIndex + 1]);
    } else {
      // All fields have been mapped
      setCurrentMappingField(null);
    }
  };
  
  const skipCurrentField = () => {
    if (!currentMappingField || currentMappingField === 'siretColumn') return;
    moveToNextField();
  };

  const handleMapColumns = async () => {
    if (!importedFile) {
      setError('Aucun fichier importé');
      return;
    }

    if (!columnMapping.siretColumn) {
      setError('La colonne SIRET est obligatoire');
      return;
    }

    try {
      await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/adherents/import/map/${importedFile.id}`,
        columnMapping,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      // Move to the next step
      setCurrentStep(3);
    } catch (error: any) {
      console.error('Error mapping columns:', error);
      setError(
        error.response?.data?.message ||
          'Une erreur est survenue lors du mapping des colonnes'
      );
    }
  };

  const handleProcessFile = async () => {
    if (!importedFile) {
      setError('Aucun fichier importé');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/adherents/import/process/${importedFile.id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      setProcessingResult(response.data);
      // Move to the next step
      setCurrentStep(4);
    } catch (error: any) {
      console.error('Error processing file:', error);
      setError(
        error.response?.data?.message ||
          'Une erreur est survenue lors du traitement du fichier'
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const renderStepOne = () => {
    const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    };
    
    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
    };
    
    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
    };
    
    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const droppedFile = e.dataTransfer.files[0];
        const fileExtension = droppedFile.name.split('.').pop()?.toLowerCase();
        
        if (fileExtension && ['xlsx', 'xls', 'csv'].includes(fileExtension)) {
          setFile(droppedFile);
        } else {
          setError('Format de fichier non supporté. Veuillez utiliser un fichier Excel (.xlsx, .xls) ou CSV.');
        }
      }
    };
    
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Étape 1: Sélection du fichier</h2>
        
        <div className="mb-6 space-y-4">
          <div className="p-4 bg-blue-50 rounded-md">
            <h3 className="font-medium text-blue-800 mb-2">Instructions</h3>
            <ul className="list-disc list-inside text-sm text-blue-700 space-y-1">
              <li>Formats supportés: Excel (.xlsx, .xls) ou CSV</li>
              <li>Votre fichier doit contenir au minimum une colonne avec les numéros SIRET (14 chiffres)</li>
              <li>Les colonnes suivantes sont optionnelles: nom du contact, téléphone, email</li>
              <li>Assurez-vous que la première ligne contient les en-têtes de colonnes</li>
            </ul>
            
            <div className="mt-3 p-2 bg-blue-100 rounded-md">
              <h4 className="font-medium text-blue-800 mb-1">Pour les fichiers CSV :</h4>
              <ul className="list-disc list-inside text-xs text-blue-700 space-y-1">
                <li>Utilisez de préférence l'encodage UTF-8</li>
                <li>Les séparateurs acceptés sont les virgules (,) ou les points-virgules (;)</li>
                <li>Si vous exportez depuis Excel, choisissez "CSV (séparateur: point-virgule)"</li>
                <li>En cas de problème d'encodage, essayez de sauvegarder votre fichier en UTF-8</li>
              </ul>
            </div>
          </div>
          
          <div 
            className={`border-2 border-dashed rounded-lg p-8 text-center ${
              isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="space-y-2">
              <svg 
                className="mx-auto h-12 w-12 text-gray-400" 
                stroke="currentColor" 
                fill="none" 
                viewBox="0 0 48 48" 
                aria-hidden="true"
              >
                <path 
                  d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                />
              </svg>
              <div className="flex text-sm text-gray-600">
                <label
                  htmlFor="file-upload"
                  className="relative cursor-pointer rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none"
                >
                  <span>Télécharger un fichier</span>
                  <input
                    id="file-upload"
                    name="file-upload"
                    type="file"
                    className="sr-only"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept=".xlsx,.xls,.csv"
                  />
                </label>
                <p className="pl-1">ou glisser-déposer</p>
              </div>
              <p className="text-xs text-gray-500">
                Excel ou CSV jusqu'à 10MB
              </p>
            </div>
          </div>
        </div>
        
        {file && (
          <div className="mb-6 p-4 bg-green-50 rounded-md">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="font-medium text-green-800">Fichier sélectionné</h3>
            </div>
            <div className="mt-2 text-sm text-green-700">
              <p><span className="font-medium">Nom:</span> {file.name}</p>
              <p><span className="font-medium">Taille:</span> {(file.size / 1024).toFixed(2)} KB</p>
              <p><span className="font-medium">Type:</span> {file.type || 'Non spécifié'}</p>
            </div>
          </div>
        )}

        {isUploading && (
          <div className="mb-4">
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-blue-600 h-2.5 rounded-full"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <p className="mt-2 text-sm text-gray-600 text-center">
              {uploadProgress}% téléchargé
            </p>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
            {error}
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={handleFileUpload}
            disabled={!file || isUploading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {isUploading ? 'Téléchargement...' : 'Télécharger et continuer'}
          </button>
        </div>
      </div>
    );
  };

  const renderStepTwo = () => {
    // Field labels and descriptions
    const fieldInfo: Record<keyof ColumnMapping, {
      label: string;
      description: string;
      required: boolean;
    }> = {
      siretColumn: {
        label: 'SIRET de l\'établissement',
        description: 'Sélectionnez la colonne contenant le numéro SIRET (14 chiffres)',
        required: true
      },
      contactNameColumn: {
        label: 'Nom du contact',
        description: 'Sélectionnez la colonne contenant le nom du contact dans l\'établissement',
        required: false
      },
      contactPhoneColumn: {
        label: 'Téléphone du contact',
        description: 'Sélectionnez la colonne contenant le numéro de téléphone du contact',
        required: false
      },
      contactEmailColumn: {
        label: 'Email du contact',
        description: 'Sélectionnez la colonne contenant l\'adresse email du contact',
        required: false
      },
      descriptionColumns: {
        label: 'Champs additionnels pour la description',
        description: 'Sélectionnez une ou plusieurs colonnes à inclure dans la description (cliquez sur plusieurs colonnes)',
        required: false
      }
    };

    // Get current field info
    const currentField = currentMappingField ? fieldInfo[currentMappingField] : null;
    
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Étape 2: Mapping des colonnes</h2>
        
        {parseError && (
          <div className="mb-6 p-4 bg-red-100 rounded-md">
            <h3 className="font-medium text-red-800 mb-2">Erreur de lecture du fichier</h3>
            <p className="text-red-700">{parseError}</p>
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <h4 className="font-medium text-yellow-800 mb-1">Suggestions pour résoudre le problème :</h4>
              <ul className="list-disc list-inside text-sm text-yellow-700 space-y-1">
                <li>Vérifiez que votre fichier CSV utilise un encodage standard (UTF-8 ou ISO-8859-1)</li>
                <li>Assurez-vous que les séparateurs sont des virgules ou des points-virgules</li>
                <li>Essayez d'ouvrir votre fichier CSV dans un éditeur de texte et de le sauvegarder avec l'encodage UTF-8</li>
                <li>Si vous utilisez Excel, essayez d'exporter à nouveau en format CSV (séparateur: point-virgule)</li>
                <li>Vérifiez que la première ligne contient bien les en-têtes de colonnes</li>
              </ul>
            </div>
          </div>
        )}
        
        {filePreview && !parseError && (
          <>
            {/* Progress indicator */}
            <div className="mb-6">
              <div className="flex items-center mb-2">
                {['siretColumn', 'contactNameColumn', 'contactPhoneColumn', 'contactEmailColumn', 'descriptionColumns'].map((field, index) => (
                  <React.Fragment key={field}>
                    <div 
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-xs
                        ${currentMappingField === field 
                          ? 'bg-blue-600 text-white' 
                          : columnMapping[field as keyof ColumnMapping] || 
                            (field === 'descriptionColumns' && columnMapping.descriptionColumns.length > 0)
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-200 text-gray-600'
                        }`}
                    >
                      {index + 1}
                    </div>
                    {index < 4 && (
                      <div className={`flex-1 h-1 mx-1 ${
                        (columnMapping[field as keyof ColumnMapping] || 
                         (field === 'descriptionColumns' && columnMapping.descriptionColumns.length > 0))
                          ? 'bg-green-500'
                          : 'bg-gray-200'
                      }`}></div>
                    )}
                  </React.Fragment>
                ))}
              </div>
              <div className="flex justify-between text-xs text-gray-500">
                <span>SIRET</span>
                <span>Nom</span>
                <span>Téléphone</span>
                <span>Email</span>
                <span>Description</span>
              </div>
            </div>
            
            {/* Current field mapping instructions */}
            {currentField && (
              <div className="mb-6 p-4 bg-blue-50 rounded-md">
                <h3 className="font-medium text-blue-800 mb-1">{currentField.label} {currentField.required && <span className="text-red-500">*</span>}</h3>
                <p className="text-blue-700 text-sm mb-2">{currentField.description}</p>
                
                {currentMappingField === 'descriptionColumns' ? (
                  <p className="text-sm text-blue-700">
                    Colonnes sélectionnées: {columnMapping.descriptionColumns.length > 0 
                      ? columnMapping.descriptionColumns.join(', ') 
                      : 'Aucune'}
                  </p>
                ) : (
                  <p className="text-sm text-blue-700">
                    Colonne sélectionnée: {currentMappingField && columnMapping[currentMappingField] || 'Aucune'}
                  </p>
                )}
                
                {currentMappingField !== 'siretColumn' && (
                  <button
                    onClick={skipCurrentField}
                    className="mt-2 text-sm text-blue-700 hover:text-blue-900 underline"
                  >
                    Ignorer ce champ
                  </button>
                )}
              </div>
            )}
            
            {/* File preview with clickable headers */}
            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-2">
                Aperçu du fichier: {filePreview.totalRows} lignes au total
                {filePreview.fileType && (
                  <span className="ml-2">
                    (Format: {filePreview.fileType === 'excel' ? 'Excel' : 'CSV'})
                  </span>
                )}
              </p>
              
              <div className="overflow-x-auto border border-gray-200 rounded-md">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {filePreview.headers.map((header, index) => (
                        <th
                          key={index}
                          onClick={() => currentMappingField && handleColumnSelect(header)}
                          className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer
                            ${currentMappingField && columnMapping[currentMappingField] === header 
                              ? 'bg-blue-100 text-blue-700 border-b-2 border-blue-500' 
                              : currentMappingField === 'descriptionColumns' && columnMapping.descriptionColumns.includes(header)
                                ? 'bg-blue-50 text-blue-600'
                                : 'text-gray-500 hover:bg-gray-100'
                            }
                          `}
                        >
                          <div className="flex items-center">
                            {header}
                            {Object.entries(columnMapping).map(([field, value]) => {
                              if (field === 'descriptionColumns') {
                                if (columnMapping.descriptionColumns.includes(header)) {
                                  return (
                                    <span key={field} className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                      Description
                                    </span>
                                  );
                                }
                              } else if (value === header) {
                                return (
                                  <span key={field} className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                    {field === 'siretColumn' ? 'SIRET' : 
                                     field === 'contactNameColumn' ? 'Nom' :
                                     field === 'contactPhoneColumn' ? 'Tél' : 'Email'}
                                  </span>
                                );
                              }
                              return null;
                            })}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filePreview.sampleData.map((row, rowIndex) => (
                      <tr key={rowIndex} className="hover:bg-gray-50">
                        {row.map((cell, cellIndex) => (
                          <td
                            key={cellIndex}
                            className={`px-6 py-4 whitespace-nowrap text-sm text-gray-500
                              ${currentMappingField && columnMapping[currentMappingField] === filePreview.headers[cellIndex]
                                ? 'bg-blue-50'
                                : currentMappingField === 'descriptionColumns' && 
                                  columnMapping.descriptionColumns.includes(filePreview.headers[cellIndex])
                                  ? 'bg-blue-50'
                                  : ''
                              }
                            `}
                          >
                            {cell !== null && cell !== undefined ? String(cell) : ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            
            {/* Navigation buttons */}
            <div className="flex justify-between">
              <button
                onClick={() => {
                  setCurrentStep(1);
                  setCurrentMappingField(null);
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Retour
              </button>
              
              {currentMappingField === 'descriptionColumns' ? (
                <button
                  onClick={handleMapColumns}
                  disabled={!columnMapping.siretColumn}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  Continuer
                </button>
              ) : !currentMappingField ? (
                <button
                  onClick={handleMapColumns}
                  disabled={!columnMapping.siretColumn}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  Continuer
                </button>
              ) : (
                <button
                  onClick={moveToNextField}
                  disabled={currentMappingField === 'siretColumn' && !columnMapping.siretColumn}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {currentMappingField === 'siretColumn' ? 'Continuer' : 'Ignorer ce champ'}
                </button>
              )}
            </div>
          </>
        )}
        
        {error && (
          <div className="mt-4 p-3 bg-red-100 text-red-700 rounded-md">
            {error}
          </div>
        )}
      </div>
    );
  };

  const renderStepThree = () => (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-xl font-semibold mb-4">Étape 3: Confirmation</h2>
      <p className="mb-6 text-gray-600">
        Vous êtes sur le point d'importer les données de vos adhérents. Veuillez confirmer pour lancer le traitement.
      </p>

      <div className="mb-6 p-4 bg-blue-50 rounded-md">
        <h3 className="font-medium text-blue-800 mb-2">Récapitulatif</h3>
        <ul className="list-disc list-inside text-sm text-blue-800 space-y-1">
          <li>Fichier: {importedFile?.originalFilename}</li>
          <li>Nombre de lignes: {filePreview?.totalRows}</li>
          <li>Colonne SIRET: {columnMapping.siretColumn}</li>
          <li>Colonne Nom du contact: {columnMapping.contactNameColumn || 'Non spécifié'}</li>
          <li>Colonne Téléphone du contact: {columnMapping.contactPhoneColumn || 'Non spécifié'}</li>
          <li>Colonne Email du contact: {columnMapping.contactEmailColumn || 'Non spécifié'}</li>
          <li>
            Colonnes pour la description:{' '}
            {columnMapping.descriptionColumns.length > 0
              ? columnMapping.descriptionColumns.join(', ')
              : 'Aucune'}
          </li>
        </ul>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
          {error}
        </div>
      )}

      <div className="flex justify-between">
        <button
          onClick={() => setCurrentStep(2)}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Retour
        </button>
        <button
          onClick={handleProcessFile}
          disabled={isProcessing}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {isProcessing ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Traitement en cours...
            </>
          ) : (
            'Lancer l\'import'
          )}
        </button>
      </div>
    </div>
  );

  const renderStepFour = () => (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-xl font-semibold mb-4">Étape 4: Résultat de l'import</h2>
      
      {processingResult && (
        <div className="mb-6 space-y-4">
          <div className="p-5 bg-green-50 rounded-md border border-green-200">
            <div className="flex items-center mb-3">
              <svg className="h-8 w-8 text-green-500 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-lg font-medium text-green-800">Import réussi</h3>
            </div>
            <div className="ml-11">
              <p className="text-green-700 mb-2">
                <span className="font-semibold">{processingResult.importedCount}</span> adhérents ont été importés avec succès.
              </p>
              <div className="flex space-x-4 text-sm">
                <div className="bg-white px-3 py-1 rounded border border-green-200">
                  <span className="text-gray-500">Fichier: </span>
                  <span className="font-medium">{importedFile?.originalFilename}</span>
                </div>
                <div className="bg-white px-3 py-1 rounded border border-green-200">
                  <span className="text-gray-500">Lignes traitées: </span>
                  <span className="font-medium">{filePreview?.totalRows}</span>
                </div>
              </div>
            </div>
          </div>
          
          {processingResult.errors && processingResult.errors.length > 0 && (
            <div className="p-5 bg-yellow-50 rounded-md border border-yellow-200">
              <div className="flex items-center mb-3">
                <svg className="h-8 w-8 text-yellow-500 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <h3 className="text-lg font-medium text-yellow-800">Avertissements ({processingResult.errors.length})</h3>
              </div>
              <div className="ml-11">
                <div className="max-h-60 overflow-y-auto bg-white p-3 rounded border border-yellow-200">
                  <ul className="list-disc list-inside text-sm text-yellow-800 space-y-1">
                    {processingResult.errors.map((error: string, index: number) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
                <p className="mt-2 text-sm text-yellow-700">
                  Ces avertissements n'ont pas empêché l'import, mais certaines données peuvent être incomplètes.
                </p>
              </div>
            </div>
          )}
          
          <div className="p-4 bg-blue-50 rounded-md">
            <h3 className="font-medium text-blue-800 mb-2">Que faire maintenant ?</h3>
            <ul className="list-disc list-inside text-sm text-blue-700 space-y-1">
              <li>Consultez la liste des adhérents pour vérifier les données importées</li>
              <li>Vous pouvez modifier manuellement les informations des adhérents si nécessaire</li>
              <li>Pour importer un autre fichier, cliquez sur le bouton ci-dessous</li>
            </ul>
          </div>
        </div>
      )}

      <div className="flex justify-between">
        <button
          onClick={() => navigate('/adherents')}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Voir la liste des adhérents
        </button>
        <button
          onClick={() => {
            setCurrentStep(1);
            setFile(null);
            setImportedFile(null);
            setFilePreview(null);
            setColumnMapping({
              siretColumn: null,
              contactNameColumn: null,
              contactPhoneColumn: null,
              contactEmailColumn: null,
              descriptionColumns: [],
            });
            setCurrentMappingField(null);
            setProcessingResult(null);
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
          }}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Importer un autre fichier
        </button>
      </div>
    </div>
  );

  const renderStepIndicator = () => (
    <div className="mb-8">
      <div className="flex items-center justify-between">
        {[
          { step: 1, label: 'Sélection du fichier', icon: 'M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
          { step: 2, label: 'Mapping des colonnes', icon: 'M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4' },
          { step: 3, label: 'Confirmation', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
          { step: 4, label: 'Résultat', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4' }
        ].map((item, index) => (
          <React.Fragment key={item.step}>
            <div className="flex flex-col items-center">
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  currentStep >= item.step
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-600'
                }`}
              >
                <svg 
                  className="w-6 h-6" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth="2" 
                    d={item.icon}
                  />
                </svg>
              </div>
              <div className="text-xs mt-2 text-center font-medium">
                {item.label}
              </div>
              {currentStep === item.step && (
                <div className="w-2 h-2 rounded-full bg-blue-600 mt-1"></div>
              )}
            </div>
            {index < 3 && (
              <div
                className={`flex-1 h-1 mx-2 ${
                  currentStep > item.step ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              ></div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Import d'adhérents</h1>
        <p className="mt-1 text-sm text-gray-500">
          Importez vos adhérents à partir d'un fichier Excel ou CSV.
        </p>
      </div>

      {renderStepIndicator()}

      {currentStep === 1 && renderStepOne()}
      {currentStep === 2 && renderStepTwo()}
      {currentStep === 3 && renderStepThree()}
      {currentStep === 4 && renderStepFour()}
    </div>
  );
};

export default ImportAdherents; 